import React, { useCallback, useEffect, useState } from 'react';
import { Mail } from 'react-feather';
import { useForm } from 'react-hook-form';
import { Image } from 'semantic-ui-react';
import TextInput from 'components/form-fields-v2/TextInput';
import { ProfileImageModal } from 'components/modals';
import { HelpTooltip } from 'components/tooltip';
import { getProfilePictureSrc, isNormalLogin } from 'helpers';
import { IFile } from 'services';
import { IUser } from 'types';
import styles from './styles/userProfile.scss';
import { ToggleButton } from 'components/quashed';

interface IProps {
  auth0: any;
  user: IUser;
  updateProfilePhoto?(file: IFile): void;
  updateUser(user: IUser): void;
}

const getDefaultFormValues = R.pick(['firstName', 'lastName']);

const UserProfileForm = ({
  user,
  auth0,
  updateProfilePhoto,
  updateUser,
}: IProps) => {
  const [openModal, setOpenModal] = useState(false);
  const [isEdit, setEdit] = useState(false);

  const picture = getProfilePictureSrc(user.picture || auth0.picture);

  const {
    formState: { errors },
    register,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: getDefaultFormValues(user),
  });

  useEffect(() => {
    reset(getDefaultFormValues(user));
  }, [user.firstName, user.lastName]);

  const closeModal = () => setOpenModal(false);
  const showModal = () => setOpenModal(true);

  const handleEdit = useCallback((e) => {
    e.preventDefault();
    setEdit(true);
  }, []);

  const canEditPicture = isNormalLogin(user.id);
  const image = (
    <Image
      className={classnames(styles.avatar, canEditPicture && styles.pointer)}
      src={picture}
      avatar={true}
    />
  );

  const submit = (data) => {
    if (data.firstName !== user.firstName || data.lastName !== user.lastName) {
      updateUser({
        ...user,
        ...data,
        name: `${data.firstName} ${data.lastName}`,
      });
    }
    setEdit(false);
  };

  return (
    <div className={styles.container}>
      <form className={styles.form} onSubmit={handleSubmit(submit)}>
        <div className={styles.fieldWrap}>
          <div className={styles.fields}>
            <div className={styles.imageContainer}>
              {canEditPicture ? (
                <ProfileImageModal
                  open={openModal}
                  onClose={closeModal}
                  onOpen={showModal}
                  trigger={image}
                  onUpload={updateProfilePhoto}
                />
              ) : (
                image
              )}
            </div>
            {isEdit ? (
              <div className={styles.names}>
                <TextInput
                  basic={true}
                  className={styles.field}
                  disabled={!isEdit}
                  error={!!errors?.firstName}
                  inputClassName={styles.input}
                  {...register('firstName', {
                    required: true,
                    pattern: /^[A-Za-z]+$/i,
                  })}
                />
                <TextInput
                  basic={true}
                  className={styles.field}
                  disabled={!isEdit}
                  error={!!errors?.lastName}
                  inputClassName={styles.input}
                  {...register('lastName', {
                    required: true,
                    pattern: /^[A-Za-z]+$/i,
                  })}
                />
              </div>
            ) : (
              <h2 className={styles.name}>
                {user.firstName} {user.lastName}
              </h2>
            )}
          </div>

          <div className={styles.email}>
            <Mail size={16} /> {user.email}
          </div>
        </div>

        <ToggleButton
          className={styles.buttons}
          onClick={handleEdit}
          toggled={isEdit}
          buttonText="Edit name"
        />
      </form>
      <div className={styles.tooltipContainer}>
        <HelpTooltip
          position="left center"
          toggleContainerClassName={styles.tooltip}
          hoverable
          content={
            <>
              We respect your privacy and understand protecting your personal
              information is important. Our Privacy Policy (available on our
              website:{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.quashed.co.nz/privacy"
              >
                www.quashed.co.nz/privacy
              </a>
              ) sets out how we collect and handle your personal information.
            </>
          }
        />
      </div>
    </div>
  );
};

export default UserProfileForm;
